import React, { useCallback, FC, useMemo } from 'react';
import { notification, confirm, Table, UserName, Button } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import {
  useLoadIntegrationTokens,
  useRevokeIntegrationToken,
  getUpdateCacheOnRevokeIntegrationToken
} from 'src/models/company/Tokens/hooks';
import { IIntegrationToken } from 'src/models/company/Tokens/types';
import { ColumnProps } from 'antd/lib/table';
import { GetComponentProps } from 'rc-table/lib/interface';
import dayjs from 'src/helpers/dayjs';
import modalStyles from 'src/common/Modal/index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

function useTableColumns(onRevokeToken: (hash: string) => void): ColumnProps<IIntegrationToken>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    return [
      {
        title: t('Common.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        className: styles.colName,
        render: (_, record) => <span>{record.name}</span>
      },
      {
        title: t('Common.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        className: styles.colCreatedAt,
        render: (_, record) => t('Common.Format.utcDateTimeFormat', { date: dayjs(record.createdAt).toDate() })
      },
      {
        title: t('Common.ACL.owner'),
        dataIndex: 'owner',
        key: 'owner',
        className: styles.colOwner,
        render: (_, record) => {
          return <UserName user={record.owner} size={'s'} />;
        }
      },
      {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          return (
            <ul className={styles.actions}>
              <li className={styles.action}>
                <Button
                  type={'text'}
                  className={styles.button}
                  onClick={() => {
                    onRevokeToken(record.hash);
                  }}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </li>
            </ul>
          );
        }
      }
    ];
  }, [onRevokeToken, t]);
}

interface IProps {}

export const TokensList: FC<IProps> = () => {
  const { t } = useTranslation();
  const { data: tokens, loading: tokensLoading } = useLoadIntegrationTokens();

  const [revokeToken] = useRevokeIntegrationToken();

  const onRevokeToken = useCallback(
    async hash => {
      confirm.confirm({
        title: t('Tokens.tokensRevokeConfirm'),
        content: t('Tokens.tokensRevokeNote'),
        okText: t('Common.yes'),
        cancelText: t('Common.no'),
        className: modalStyles.confirmModal,
        centered: true,
        onOk() {
          revokeToken({
            variables: { hash },
            update: getUpdateCacheOnRevokeIntegrationToken(hash)
          }).catch(() => {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          });
        }
      });
    },
    [revokeToken, t]
  );
  const data = useMemo(() => {
    return (
      (tokens &&
        tokens.map(
          (token): IIntegrationToken => ({
            hash: token.hash,
            name: token.name,
            owner: token.owner,
            createdAt: token.createdAt
          })
        )) ||
      []
    );
  }, [tokens]);

  const columns: ColumnProps<IIntegrationToken>[] = useTableColumns(onRevokeToken);
  const onRow: GetComponentProps<IIntegrationToken> = useCallback(() => {
    return {
      onClick: e => {
        e.stopPropagation();
      }
    };
  }, []);

  return (
    <div className={styles.tokensList}>
      {tokens && tokens?.length !== 0 && (
        <Table
          loading={tokensLoading}
          columns={columns}
          dataSource={data}
          onRow={onRow}
          rowKey={'hash'}
          rowClassName={styles.row}
          className={styles.table}
          size={'large'}
        />
      )}
    </div>
  );
};

export default TokensList;
